/**
 * ElectHQ Landing Page - Main JavaScript
 */

(function () {
  'use strict';

  // ========================================================================
  // Runtime config (can be overridden with window.ELECTHQ_CONFIG)
  // ========================================================================
  var runtimeConfig = window.ELECTHQ_CONFIG || {};
  var envConfig = typeof process !== 'undefined' && process.env ? process.env : {};

  /**
   * Pick API/admin base URLs from the page hostname so one built `dist` works on prod and local dev
   * without rebuilding. Override anytime with window.ELECTHQ_CONFIG.
   */
  function getEnvironmentDefaults() {
    var host = '';
    try {
      host = String(window.location.hostname || '').toLowerCase();
    } catch (e) {
      host = '';
    }

    // Public marketing site (production)
    if (host === 'electhq.net' || host === 'www.electhq.net') {
      return {
        API_BASE_URL: 'https://api.electhq.net',
        ADMIN_BASE_URL: 'https://admin.electhq.net'
      };
    }

    // Local, previews, staging domains, IP, file://, etc.
    return {
      API_BASE_URL: 'https://api-dev.electhq.net',
      ADMIN_BASE_URL: 'https://admin-dev.electhq.net'
    };
  }

  var envDefaults = getEnvironmentDefaults();
  var APP_CONFIG = {
    API_BASE_URL: runtimeConfig.API_BASE_URL || envConfig.API_BASE_URL || envDefaults.API_BASE_URL,
    ADMIN_BASE_URL: runtimeConfig.ADMIN_BASE_URL || envConfig.ADMIN_BASE_URL || envDefaults.ADMIN_BASE_URL,
    CONTACT_EMAIL: runtimeConfig.CONTACT_EMAIL || envConfig.CONTACT_EMAIL || 'info@vanguardai.net',
    DEMO_EMAIL: runtimeConfig.DEMO_EMAIL || envConfig.DEMO_EMAIL || 'demo@vanguardai.net'
  };

  /**
   * When you open the marketing site from this machine only (localhost / loopback), send “Sign in / Register /
   * Start trial” to a local Next.js Staff dev server so one `dist` works everywhere.
   * Deployed dev/staging/prod hostnames are unchanged → still use admin.electhq.net / admin-dev.electhq.net from
   * getEnvironmentDefaults or explicit `window.ELECTHQ_CONFIG.ADMIN_BASE_URL`.
   */
  (function applyLocalStaffWhenMarketingIsLoopback() {
    // Explicit deploy override always wins (dev / prod pipelines can set this once in HTML or tag manager).
    if (runtimeConfig.ADMIN_BASE_URL) return;

    var host = '';
    try {
      host = String(window.location.hostname || '').toLowerCase();
    } catch (e) {
      return;
    }

    // Production + any *.electhq.net staging/dev marketing host: never redirect Staff to localhost.
    if (host === 'electhq.net' || host.endsWith('.electhq.net')) return;

    // Only bare loopback — LAN IP / dev-server hostnames keep admin-dev / admin from getEnvironmentDefaults().
    var loopback =
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '::1' ||
      host === '[::1]';

    if (!loopback) return;

    var localBase = String(runtimeConfig.LOCAL_STAFF_URL || 'http://localhost:3000').replace(/\/+$/, '');
    APP_CONFIG.ADMIN_BASE_URL = localBase;
  })();

  window.APP_CONFIG = APP_CONFIG;

  // ========================================================================
  // Sign up / 7-day free-trial kill switch
  // Mirrors the Staff app (src/configs/signupDisabled.ts). When true the public
  // "Sign up" and "Start 7 days trial" CTAs are neutralised so they no longer
  // bounce the visitor to the (disabled) Staff register/checkout flow. Override
  // at runtime with window.ELECTHQ_CONFIG.SIGNUP_DISABLED = false.
  // ========================================================================
  var SIGNUP_DISABLED =
    typeof runtimeConfig.SIGNUP_DISABLED === 'boolean' ? runtimeConfig.SIGNUP_DISABLED : true;
  var SIGNUP_DISABLED_TOOLTIP =
    runtimeConfig.SIGNUP_DISABLED_TOOLTIP || 'Sign up is temporarily unavailable';

  // Neutralise a single CTA: strip its href, grey it out, and swallow clicks
  // (capture phase, so it runs before any subscribe/navigation handler).
  function markCtaDisabled(node) {
    if (!node || node.__signupDisabled) return;
    node.__signupDisabled = true;
    node.setAttribute('aria-disabled', 'true');
    node.setAttribute('title', SIGNUP_DISABLED_TOOLTIP);
    node.classList.add('is-disabled');
    node.removeAttribute('href');
    node.style.opacity = '0.5';
    node.style.cursor = 'not-allowed';
    node.addEventListener(
      'click',
      function (event) {
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  // Disable every Sign up + "Start 7 days trial" CTA across index / pricing /
  // packages (a single chokepoint so each HTML file does not need editing).
  function disableSignupAndTrialCtas() {
    if (!SIGNUP_DISABLED) return;
    var nodes = [];
    document
      .querySelectorAll(
        'a[href="sign-up.html"], .pricing-card .pricing-card__action a, .pkg-card .pkg-card__action a'
      )
      .forEach(function (n) {
        nodes.push(n);
      });
    // "Start 7 days trial" buttons on index / pricing route to packages.html and
    // are not covered by the selectors above — match them by their label.
    document.querySelectorAll('a').forEach(function (n) {
      if (String(n.textContent || '').trim().toLowerCase() === 'start 7 days trial') nodes.push(n);
    });
    nodes.forEach(markCtaDisabled);
  }

  function normalizeBaseUrl(url) {
    return String(url || '').replace(/\/+$/, '');
  }

  function getAdminPath(pathname) {
    var base = normalizeBaseUrl(APP_CONFIG.ADMIN_BASE_URL);
    var cleanPath = '/' + String(pathname || '').replace(/^\/+/, '');
    try {
      return new URL(cleanPath, base + '/').toString();
    } catch (error) {
      return base + cleanPath;
    }
  }

  /** Next.js Staff uses `[lang]` — default `en` (override via `window.ELECTHQ_CONFIG.ADMIN_DEFAULT_LANG`). */
  function getAdminDefaultLang() {
    var lang = String(runtimeConfig.ADMIN_DEFAULT_LANG || 'en')
      .trim()
      .toLowerCase()
      .replace(/[^a-z-]/g, '');
    return lang || 'en';
  }

  function getStaffRegisterUrl() {
    return getAdminPath('/' + getAdminDefaultLang() + '/register');
  }

  function getStaffLoginUrl() {
    return getAdminPath('/' + getAdminDefaultLang() + '/login');
  }

  function extractDigits(text) {
    return String(text || '').replace(/[^\d]/g, '');
  }

  function titleCase(value) {
    if (!value) return '';
    return String(value).charAt(0).toUpperCase() + String(value).slice(1);
  }

  // ========================================================================
  // Header scroll effect
  // ========================================================================
  const header = document.getElementById('header');

  function handleScroll() {
    if (window.scrollY > 10) {
      header.classList.add('header--scrolled');
    } else {
      header.classList.remove('header--scrolled');
    }
  }

  window.addEventListener('scroll', handleScroll, { passive: true });

  // ========================================================================
  // Mobile navigation
  // ========================================================================
  const hamburger = document.getElementById('hamburger');
  const mainNav = document.getElementById('mainNav');
  const mobileBreakpoint = 768;

  if (hamburger && mainNav) {
    // Reuse an existing overlay if a page already provides one.
    const overlay =
      document.querySelector('.nav-overlay') ||
      document.getElementById('navOverlay') ||
      document.createElement('div');

    if (!overlay.parentNode) {
      overlay.className = 'nav-overlay';
      document.body.appendChild(overlay);
    }

    // Keep menu behavior deterministic for QA checks.
    function setNavOpenState(isOpen) {
      mainNav.classList.toggle('open', isOpen);
      overlay.classList.toggle('active', isOpen);
      document.body.style.overflow = isOpen ? 'hidden' : '';
      hamburger.setAttribute('aria-expanded', String(isOpen));
      hamburger.classList.toggle('active', isOpen);
    }

    function openNav() {
      setNavOpenState(true);
    }

    function closeNav() {
      setNavOpenState(false);
    }

    hamburger.setAttribute('aria-expanded', 'false');
    hamburger.setAttribute('aria-controls', 'mainNav');

    hamburger.addEventListener('click', function () {
      if (mainNav.classList.contains('open')) {
        closeNav();
      } else {
        openNav();
      }
    });

    overlay.addEventListener('click', closeNav);

    // Close nav on link click
    var navLinks = mainNav.querySelectorAll('.header__nav-link');
    navLinks.forEach(function (link) {
      link.addEventListener('click', closeNav);
    });

    // Close nav on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && mainNav.classList.contains('open')) {
        closeNav();
      }
    });

    // Prevent sticky open state when switching from mobile to desktop width.
    window.addEventListener('resize', function () {
      if (window.innerWidth > mobileBreakpoint && mainNav.classList.contains('open')) {
        closeNav();
      }
    });
  }

  // ========================================================================
  // Smooth scroll for anchor links
  // ========================================================================
  document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
    anchor.addEventListener('click', function (e) {
      var targetId = this.getAttribute('href');
      if (targetId === '#') return;

      var target = document.querySelector(targetId);
      if (target && header) {
        e.preventDefault();
        var headerHeight = header.offsetHeight;
        var targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight;
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    });
  });

  // ========================================================================
  // Scroll-based animations (Intersection Observer)
  // ========================================================================
  var animateElements = document.querySelectorAll(
    '.feature-card, .pricing-card, .mission__text, .hero__title, .features__title'
  );

  if ('IntersectionObserver' in window) {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    animateElements.forEach(function (el) {
      el.style.opacity = '0';
      observer.observe(el);
    });
  }

  // ========================================================================
  // Pricing toggle (Monthly / Annually)
  // ========================================================================
  var pricingCards = document.querySelectorAll('.pricing-card');
  var monthlyPrices = ['$99', '$299', '$999'];
  var annualPrices = ['$950', '$2,870', '$9,600'];

  pricingCards.forEach(function (card, index) {
    var toggle = card.querySelector('.toggle-switch__input');
    var toggleSwitch = card.querySelector('.toggle-switch');
    var priceEl = card.querySelector('.pricing-card__price');
    var monthlyLabel = card.querySelector('.toggle-option:first-child');
    var annuallyLabel = card.querySelector('.toggle-option:last-child');
    var monthlyPrice = card.getAttribute('data-monthly-price') || monthlyPrices[index];
    var annualPrice = card.getAttribute('data-annual-price') || annualPrices[index];

    if (toggle) {
      function syncPricingState(isAnnual) {
        if (isAnnual) {
          priceEl.textContent = annualPrice;
          if (monthlyLabel) monthlyLabel.classList.remove('toggle-option--active');
          if (annuallyLabel) annuallyLabel.classList.add('toggle-option--active');
        } else {
          priceEl.textContent = monthlyPrice;
          if (monthlyLabel) monthlyLabel.classList.add('toggle-option--active');
          if (annuallyLabel) annuallyLabel.classList.remove('toggle-option--active');
        }
      }

      function setBillingState(isAnnual) {
        if (toggle.checked === isAnnual) {
          syncPricingState(isAnnual);
          return;
        }

        toggle.checked = isAnnual;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
      }

      toggle.addEventListener('change', function () {
        syncPricingState(this.checked);
      });

      if (toggleSwitch) {
        toggleSwitch.addEventListener('click', function (event) {
          event.preventDefault();
          setBillingState(!toggle.checked);
        });
      }

      if (monthlyLabel) {
        monthlyLabel.addEventListener('click', function (event) {
          event.preventDefault();
          setBillingState(false);
        });
      }

      if (annuallyLabel) {
        annuallyLabel.addEventListener('click', function (event) {
          event.preventDefault();
          setBillingState(true);
        });
      }

      syncPricingState(toggle.checked);
    }
  });

  // ========================================================================
  // External auth links (local auth pages deprecated)
  // ========================================================================
  function hydrateAuthLinks() {
    var signInUrl = getStaffLoginUrl();

    // Sign in stays available; only Sign up is gated.
    document.querySelectorAll('a[href="sign-in.html"]').forEach(function (link) {
      link.setAttribute('href', signInUrl);
    });

    // When sign up is disabled, leave the sign-up links for
    // disableSignupAndTrialCtas() to neutralise instead of pointing them at the
    // (disabled) Staff register page.
    if (SIGNUP_DISABLED) return;

    var registerUrl = getStaffRegisterUrl();
    document.querySelectorAll('a[href="sign-up.html"]').forEach(function (link) {
      link.setAttribute('href', registerUrl);
    });
  }

  // ========================================================================
  // Pricing subscribe URL (replicate legacy flow to admin register)
  // ========================================================================
  function getPlanTypeFromCard(card) {
    var planNameEl = card.querySelector('.pricing-card__name, .pkg-card__name');
    var raw = (planNameEl ? planNameEl.textContent : '').trim().toLowerCase();
    if (raw === 'local') return 'local';
    if (raw === 'regional') return 'regional';
    if (raw === 'statewide') return 'statewide';
    return 'unknown';
  }

  function getVoterValueFromCard(card) {
    var votersEl = card.querySelector('.pricing-card__voters');
    if (votersEl) {
      var text = votersEl.textContent.toLowerCase();
      if (text.indexOf('statewide') >= 0) return 'statewide';
      var fromText = extractDigits(text);
      if (fromText) return fromText;
    }
    // packages.html (.pkg-card) has no voters line — align with Staff register query expectations
    var plan = getPlanTypeFromCard(card);
    if (plan === 'local') return '50000';
    if (plan === 'regional') return '500000';
    if (plan === 'statewide') return 'statewide';
    return '';
  }

  function getBillingValueFromCard(card) {
    var toggle = card.querySelector('.toggle-switch__input');
    if (toggle) return toggle.checked ? 'annual' : 'monthly';

    var periodEl = card.querySelector('.pkg-card__price-period');
    if (periodEl && String(periodEl.textContent).toLowerCase().indexOf('month') >= 0) {
      return 'monthly';
    }

    return 'monthly';
  }

  function getPriceFromCard(card) {
    var priceEl = card.querySelector('.pricing-card__price, .pkg-card__price-amount');
    return extractDigits(priceEl ? priceEl.textContent : '');
  }

  function buildSubscribeUrl(card) {
    var params = new URLSearchParams({
      plan: getPlanTypeFromCard(card),
      voters: getVoterValueFromCard(card),
      billing: getBillingValueFromCard(card),
      price: getPriceFromCard(card)
    });
    return getStaffRegisterUrl() + '?' + params.toString();
  }

  function initSubscribeButtons() {
    // Trial/subscribe navigation is off when sign up is disabled — the buttons
    // are neutralised by disableSignupAndTrialCtas() instead.
    if (SIGNUP_DISABLED) return;
    document
      .querySelectorAll('.pricing-card .pricing-card__action a, .pkg-card .pkg-card__action a')
      .forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          var card = button.closest('.pricing-card, .pkg-card');
          if (!card) return;
          window.location.href = buildSubscribeUrl(card);
        });
      });
  }

  // ========================================================================
  // Contact form submission
  // ========================================================================
  function getFormStatusNode(form) {
    var statusNode = form.querySelector('.form-status');
    if (statusNode) return statusNode;

    statusNode = document.createElement('div');
    statusNode.className = 'form-status';
    statusNode.setAttribute('aria-live', 'polite');
    form.appendChild(statusNode);
    return statusNode;
  }

  function setFormStatus(form, type, message) {
    var statusNode = getFormStatusNode(form);
    statusNode.className = 'form-status form-status--' + type;
    statusNode.textContent = message;
  }

  function clearFormStatus(form) {
    var statusNode = form.querySelector('.form-status');
    if (statusNode) {
      statusNode.className = 'form-status';
      statusNode.textContent = '';
    }
  }

  async function sendMail(payload) {
    var response = await fetch(normalizeBaseUrl(APP_CONFIG.API_BASE_URL) + '/api/send-mail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      var errorBody = {};
      try {
        errorBody = await response.json();
      } catch (ignore) {
        errorBody = {};
      }
      throw new Error(errorBody.message || 'Send mail request failed');
    }
  }

  function initContactFormSubmit() {
    var form = document.querySelector('.contact-form');
    if (!form) return;

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      clearFormStatus(form);

      var submitButton = form.querySelector('button[type="submit"]');
      var originalText = submitButton ? submitButton.textContent : '';
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Sending...';
      }

      var formData = new FormData(form);
      var name = String(formData.get('name') || '').trim();
      var email = String(formData.get('email') || '').trim();
      var subject = String(formData.get('subject') || '').trim();
      var message = String(formData.get('message') || '').trim();

      var mailSubject = 'Contact Form Submission - ElectHQ';
      var mailBody = [
        'Contact form submission',
        '',
        'Name: ' + name,
        'Email: ' + email,
        'Subject: ' + subject,
        'Message: ' + message
      ].join('\n');

      try {
        await sendMail({
          email: APP_CONFIG.CONTACT_EMAIL,
          subject: mailSubject,
          content: mailBody
        });

        setFormStatus(form, 'success', 'Message sent successfully. Our team will contact you soon.');
        form.reset();
      } catch (error) {
        setFormStatus(form, 'error', 'Failed to send message. Please try again.');
        console.error('[contact-form]', error);
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalText;
        }
      }
    });
  }

  // ========================================================================
  // Demo form submission
  // ========================================================================
  function initDemoFormSubmit() {
    var form = document.querySelector('.demo-form');
    if (!form) return;

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      clearFormStatus(form);

      var submitButton = form.querySelector('button[type="submit"]');
      var originalText = submitButton ? submitButton.textContent : '';
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Sending...';
      }

      var formData = new FormData(form);
      var payload = {
        name: String(formData.get('name') || '').trim(),
        email: String(formData.get('email') || '').trim(),
        phone: String(formData.get('phone') || '').trim(),
        campaign_type: String(formData.get('campaign_type') || '').trim(),
        preferred_date: String(formData.get('preferred_date') || '').trim(),
        message: String(formData.get('message') || '').trim()
      };

      var demoSubject = 'Demo Request - ElectHQ';
      var demoBody = [
        'Demo request submission',
        '',
        'Name: ' + payload.name,
        'Email: ' + payload.email,
        'Phone: ' + payload.phone,
        'Campaign Type: ' + titleCase(payload.campaign_type),
        'Preferred Date: ' + payload.preferred_date,
        'Message: ' + payload.message
      ].join('\n');

      try {
        await sendMail({
          email: APP_CONFIG.DEMO_EMAIL,
          subject: demoSubject,
          content: demoBody
        });

        setFormStatus(form, 'success', 'Demo request submitted. We will reach out shortly.');
        form.reset();
      } catch (error) {
        setFormStatus(form, 'error', 'Failed to submit demo request. Please try again.');
        console.error('[demo-form]', error);
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalText;
        }
      }
    });
  }

  function initContactFigmaForm() {
    var form = document.querySelector('.contact-figma__form');
    if (!form) return;

    var textarea = form.querySelector('.contact-figma__textarea');
    var counter = form.querySelector('.contact-figma__counter');
    if (textarea && counter) {
      function syncCounter() {
        var len = textarea.value.length;
        var max = Number(textarea.getAttribute('maxlength')) || 1000;
        counter.textContent = len + '/' + max;
      }
      textarea.addEventListener('input', syncCounter);
      syncCounter();
    }

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      clearFormStatus(form);

      var submitButton = form.querySelector('button[type="submit"]');
      var originalText = submitButton ? submitButton.textContent : '';
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Sending...';
      }

      var formData = new FormData(form);
      var first = String(formData.get('first_name') || '').trim();
      var last = String(formData.get('last_name') || '').trim();
      var email = String(formData.get('email') || '').trim();
      var message = String(formData.get('message') || '').trim();

      var mailBody = [
        'Contact form submission',
        '',
        'Name: ' + first + ' ' + last,
        'Email: ' + email,
        'Message: ' + message
      ].join('\n');

      try {
        await sendMail({
          email: APP_CONFIG.CONTACT_EMAIL,
          subject: 'Contact Form Submission - ElectHQ',
          content: mailBody
        });

        setFormStatus(form, 'success', 'Message sent successfully. Our team will contact you soon.');
        form.reset();
        if (textarea && counter) {
          counter.textContent = '0/' + (Number(textarea.getAttribute('maxlength')) || 1000);
        }
      } catch (error) {
        setFormStatus(form, 'error', 'Failed to send message. Please try again.');
        console.error('[contact-figma-form]', error);
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalText;
        }
      }
    });
  }

  function initDemoFigmaForm() {
    var form = document.querySelector('.demo-figma__form');
    if (!form) return;

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      clearFormStatus(form);

      var submitButton = form.querySelector('button[type="submit"]');
      var originalHtml = submitButton ? submitButton.innerHTML : '';
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Sending...';
      }

      var formData = new FormData(form);
      var first = String(formData.get('first_name') || '').trim();
      var last = String(formData.get('last_name') || '').trim();
      var email = String(formData.get('email') || '').trim();

      var demoSubject = 'Demo Request - ElectHQ';
      var demoBody = [
        'Demo request submission (book demo page)',
        '',
        'Name: ' + first + ' ' + last,
        'Email: ' + email
      ].join('\n');

      try {
        await sendMail({
          email: APP_CONFIG.DEMO_EMAIL,
          subject: demoSubject,
          content: demoBody
        });

        setFormStatus(form, 'success', 'Demo request submitted. We will reach out shortly.');
        form.reset();
      } catch (error) {
        setFormStatus(form, 'error', 'Failed to submit demo request. Please try again.');
        console.error('[demo-figma-form]', error);
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.innerHTML = originalHtml;
        }
      }
    });
  }

  function initElectAIAssistant() {
    var assistant = document.getElementById('electaiAssistant');
    var toggleButton = document.getElementById('electaiAssistantToggle');
    var heroToggleButton = document.getElementById('heroElectAIToggle');
    var closeButton = document.getElementById('electaiAssistantClose');
    var frame = document.getElementById('electaiAssistantFrame');

    if (!assistant || !closeButton || !frame || (!toggleButton && !heroToggleButton)) return;

    function openAssistant() {
      assistant.classList.add('electai-assistant--open');
      frame.classList.add('electai-assistant__frame--open');
      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', 'true');
      }
      if (heroToggleButton) {
        heroToggleButton.setAttribute('aria-expanded', 'true');
      }
    }

    function closeAssistant() {
      assistant.classList.remove('electai-assistant--open');
      frame.classList.remove('electai-assistant__frame--open');
      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', 'false');
      }
      if (heroToggleButton) {
        heroToggleButton.setAttribute('aria-expanded', 'false');
      }
    }

    if (toggleButton) {
      toggleButton.addEventListener('click', function (event) {
        event.preventDefault();
        openAssistant();
      });
    }

    if (heroToggleButton) {
      heroToggleButton.addEventListener('click', function (event) {
        event.preventDefault();
        openAssistant();
      });
    }

    closeButton.addEventListener('click', function (event) {
      event.preventDefault();
      closeAssistant();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && frame.classList.contains('electai-assistant__frame--open')) {
        closeAssistant();
      }
    });

    document.addEventListener('click', function (event) {
      if (!frame.classList.contains('electai-assistant__frame--open')) return;
      var clickInsideFrame = frame.contains(event.target);
      var clickOnToggle = toggleButton && toggleButton.contains(event.target);
      var clickOnHeroToggle = heroToggleButton && heroToggleButton.contains(event.target);
      if (!clickInsideFrame && !clickOnToggle && !clickOnHeroToggle) {
        closeAssistant();
      }
    });

    frame.addEventListener('click', function (event) {
      event.stopPropagation();
    });
  }

  function initCustomPlanModal() {
    var modal = document.getElementById('custom-plan-modal');
    var form = document.getElementById('custom-plan-form');
    if (!modal || !form) return;

    var selectedPlanType = 'custom';

    function openModal(trigger) {
      selectedPlanType = (trigger && trigger.getAttribute('data-plan-type')) || 'custom';
      var voterCount = trigger && trigger.getAttribute('data-voters');
      var voterInput = form.querySelector('[name="number_of_voters"]');
      if (voterCount && voterInput && !voterInput.value) {
        voterInput.value = voterCount;
      }
      modal.classList.add('modal--active');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      modal.classList.remove('modal--active');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      form.reset();
      clearFormStatus(form);
    }

    document.querySelectorAll('[data-modal-trigger="custom-plan-modal"]').forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        openModal(trigger);
      });
    });

    modal.querySelectorAll('[data-modal-close]').forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        closeModal();
      });
    });

    modal.addEventListener('click', function (event) {
      if (event.target === modal) {
        closeModal();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modal.classList.contains('modal--active')) {
        closeModal();
      }
    });

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      clearFormStatus(form);

      var submitButton = form.querySelector('button[type="submit"]');
      var originalText = submitButton ? submitButton.textContent : '';
      var formData = new FormData(form);
      var payload = {
        email: String(formData.get('email') || '').trim().toLowerCase(),
        first_name: String(formData.get('first_name') || '').trim(),
        last_name: String(formData.get('last_name') || '').trim(),
        number_of_voters: Number(formData.get('number_of_voters') || 0),
        notes: String(formData.get('notes') || '').trim() || null,
        metadata: {
          source: 'landing_custom_plan_modal',
          package_type: selectedPlanType
        }
      };

      if (!payload.first_name || !payload.last_name || !payload.email || !payload.number_of_voters) {
        setFormStatus(form, 'error', 'Please fill in all required fields.');
        return;
      }

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Submitting...';
      }

      try {
        var response = await fetch(normalizeBaseUrl(APP_CONFIG.API_BASE_URL) + '/api/custom-plan-requests', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        var result = {};
        try {
          result = await response.json();
        } catch (ignore) {
          result = {};
        }

        if (!response.ok) {
          throw new Error(result.message || 'Custom plan request failed');
        }

        setFormStatus(form, 'success', 'Custom plan request submitted. Our team will contact you soon.');
        window.setTimeout(closeModal, 1200);
      } catch (error) {
        setFormStatus(form, 'error', 'Failed to submit custom plan request. Please try again.');
        console.error('[custom-plan-modal]', error);
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalText;
        }
      }
    });
  }

  hydrateAuthLinks();
  initSubscribeButtons();
  disableSignupAndTrialCtas();
  initContactFormSubmit();
  initDemoFormSubmit();
  initContactFigmaForm();
  initDemoFigmaForm();
  initElectAIAssistant();
  initCustomPlanModal();
})();
